import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Hole, Player, Score } from "../types";

interface Props {
  player: Player;
  hole: Hole;
  existing: Score | undefined | null;
  anchorRect: DOMRect;
  // Whether to surface the putts wheel alongside score. Only true when a
  // putt-aware game (Putting, Oosler/Woosler) is active; otherwise the
  // picker stays single-column and the saved Score carries through whatever
  // putts value already lived there (typically `undefined`).
  showPutts: boolean;
  // bunkerUd: true when the "1UD" wheel item is selected. The numeric
  // putts value sent up is always 1 in that case; the flag is what tells
  // the Putting runner to also tally a U&D event for the hole.
  onSave: (gross: number, putts?: number, bunkerUd?: boolean) => void;
  onClear: () => void;
  onClose: () => void;
}

const ITEM_HEIGHT = 40;
const VISIBLE_COUNT = 3;
const WHEEL_WIDTH = 64;
const WHEEL_GAP = 8;
const PADDING_X = 12;
const LABEL_HEIGHT = 18;

const MIN_SCORE = 1;
const MAX_SCORE = 12;
const MIN_PUTTS = 0;
const MAX_PUTTS = 5;
const DEFAULT_PUTTS = 2;

// Sentinels for "clear this score" / "no putts entry" — rendered as "—"
// at the top of each wheel. Picked as -1 so they never collide with a
// valid count and still pack into the same number-typed wheel. NULL_SCORE
// triggers onClear; NULL_PUTTS translates to `undefined` on save (the
// hole's score keeps a putts value of "not entered" rather than 0, since
// 0 is a legitimate count for chip-ins / off-green putts).
const NULL_SCORE = -1;
const NULL_PUTTS = -1;
// Wheel sentinel for the "1UD" entry — bunker up-and-down + closing
// 1-putt. Uses 1.5 (non-integer so it can't collide with a real putt
// count) and is translated at save time to { putts: 1, bunkerUd: true }.
const BUNKER_UD_PUTTS = 1.5;

interface WheelItem {
  value: number;
  label: string;
}

const SCORE_ITEMS: WheelItem[] = [
  { value: NULL_SCORE, label: "—" },
  ...Array.from({ length: MAX_SCORE - MIN_SCORE + 1 }, (_, i) => ({
    value: MIN_SCORE + i,
    label: String(MIN_SCORE + i),
  })),
];
// Wheel order is: —, 0, 1UD, 1, 2, 3, 4, 5. "1UD" sits between 0 and 1
// (per spec) so a player who chipped to gimme range can quickly flick
// from a chip-in (0) to a bunker U&D + 1-putt (1UD) without scrolling
// past 1.
const PUTTS_ITEMS: WheelItem[] = [
  { value: NULL_PUTTS, label: "—" },
  { value: 0, label: "0" },
  { value: BUNKER_UD_PUTTS, label: "1UD" },
  ...Array.from({ length: MAX_PUTTS - MIN_PUTTS }, (_, i) => ({
    value: MIN_PUTTS + 1 + i,
    label: String(MIN_PUTTS + 1 + i),
  })),
];

export default function ScoreScrollPicker({
  player,
  hole,
  existing,
  anchorRect,
  showPutts,
  onSave,
  onClear,
  onClose,
}: Props) {
  const initialScore = existing?.gross ?? hole.par;
  // Only seed the putts wheel with a default when it's actually being shown.
  // When the wheel is hidden, preserve whatever putts value already lives on
  // the score (likely undefined for fresh entries) so we don't write a count
  // the user never picked. An existing bunkerUd flag snaps the wheel to the
  // 1UD row so re-tapping a finished hole shows the chosen entry.
  const initialPutts = showPutts
    ? existing?.bunkerUd
      ? BUNKER_UD_PUTTS
      : existing?.putts ?? DEFAULT_PUTTS
    : existing?.putts;

  const [score, setScore] = useState<number>(initialScore);
  const [putts, setPutts] = useState<number | undefined>(initialPutts);
  const scoreRef = useRef(score);
  scoreRef.current = score;
  const puttsRef = useRef(putts);
  puttsRef.current = putts;

  const overlayRef = useRef<HTMLDivElement>(null);
  const armedRef = useRef(false);

  // Arm the outside-click handler one frame after open so the opening
  // tap doesn't immediately close us.
  useLayoutEffect(() => {
    const t = setTimeout(() => {
      armedRef.current = true;
    }, 0);
    return () => clearTimeout(t);
  }, []);

  // Commit whatever's in the refs and close. NULL_SCORE means the user
  // picked "—" on the score wheel → clear instead. NULL_PUTTS on the
  // putts wheel → save with putts undefined (i.e. "no entry"), distinct
  // from a 0-putts chip-in. Hoisted out of the outside-click effect so
  // the per-wheel tap handlers below can reuse the exact same path.
  const commitAndClose = useCallback(() => {
    if (scoreRef.current === NULL_SCORE) {
      onClear();
    } else {
      // 1UD on the wheel translates to (putts=1, bunkerUd=true). Any
      // other wheel value passes through as a normal putts count with
      // bunkerUd left undefined.
      const wheelPutts = puttsRef.current;
      let puttsOut: number | undefined;
      let bunkerUd: boolean | undefined;
      if (wheelPutts === NULL_PUTTS) {
        puttsOut = undefined;
      } else if (wheelPutts === BUNKER_UD_PUTTS) {
        puttsOut = 1;
        bunkerUd = true;
      } else {
        puttsOut = wheelPutts;
      }
      onSave(scoreRef.current, puttsOut, bunkerUd);
    }
    onClose();
  }, [onSave, onClear, onClose]);

  // Tap handlers — when the user taps any item in either wheel, snap
  // that wheel's value to the tapped item AND commit. Refs are updated
  // synchronously alongside setState so commitAndClose reads the freshly
  // chosen value, not the pre-tap state.
  const onScoreTap = useCallback(
    (value: number) => {
      scoreRef.current = value;
      setScore(value);
      commitAndClose();
    },
    [commitAndClose],
  );

  const onPuttsTap = useCallback(
    (value: number) => {
      puttsRef.current = value;
      setPutts(value);
      commitAndClose();
    },
    [commitAndClose],
  );

  // Outside click / Escape / Enter → commit current scrolled values.
  useEffect(() => {
    const onPointer = (e: MouseEvent | TouchEvent) => {
      if (!armedRef.current) return;
      const target = e.target as Node;
      if (overlayRef.current && overlayRef.current.contains(target)) return;
      commitAndClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "Enter") commitAndClose();
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("touchstart", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("touchstart", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [commitAndClose, onClose]);

  // Position the picker centred on the anchored cell, clamped inside
  // the viewport. Width follows wheel count — single column when putts
  // are hidden so the resting picker stays tight against the cell.
  const pickerW = showPutts
    ? WHEEL_WIDTH * 2 + WHEEL_GAP + PADDING_X * 2
    : WHEEL_WIDTH + PADDING_X * 2;
  const wheelH = VISIBLE_COUNT * ITEM_HEIGHT;
  const pickerH = wheelH + LABEL_HEIGHT + PADDING_X * 2;

  const cellCenterX = anchorRect.left + anchorRect.width / 2;
  const cellCenterY = anchorRect.top + anchorRect.height / 2;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = cellCenterX - pickerW / 2;
  let top = cellCenterY - pickerH / 2;
  if (left < 4) left = 4;
  if (left + pickerW > vw - 4) left = vw - 4 - pickerW;
  if (top < 4) top = 4;
  if (top + pickerH > vh - 4) top = vh - 4 - pickerH;

  return (
    <div className="cg-score-pick-backdrop" aria-hidden>
      <div
        ref={overlayRef}
        className="cg-score-pick cg-score-pick-script"
        role="dialog"
        aria-label={`Score for hole ${hole.number}, ${player.name}`}
        style={{ left, top, width: pickerW, height: pickerH }}
      >
        <div className="cg-score-pick-cols">
          <Wheel
            label="Score"
            items={SCORE_ITEMS}
            value={score}
            onChange={setScore}
            onTap={onScoreTap}
          />
          {showPutts && (
            <Wheel
              label="Putts"
              items={PUTTS_ITEMS}
              value={putts ?? DEFAULT_PUTTS}
              onChange={setPutts}
              onTap={onPuttsTap}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Single-column wheel ────────────────────────────────────────────────────

interface WheelProps {
  label: string;
  items: WheelItem[];
  value: number;
  onChange: (next: number) => void;
  // Tapping any item commits + closes the picker. Distinct from
  // onChange so scroll-settled updates and explicit taps stay separable.
  onTap: (value: number) => void;
}

function Wheel({ label, items, value, onChange, onTap }: WheelProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef(value);
  valueRef.current = value;

  // Initial scroll to current value.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const idx = items.findIndex((it) => it.value === value);
    if (idx >= 0) el.scrollTop = idx * ITEM_HEIGHT;
    // intentionally only on mount — subsequent value changes come from
    // user scroll, where the scrollTop is already correct.
  }, []);

  // Debounced "scroll settled" → bubble the new value up.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const handle = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        const idx = Math.round(el.scrollTop / ITEM_HEIGHT);
        const clamped = Math.max(0, Math.min(items.length - 1, idx));
        const next = items[clamped]?.value;
        if (next !== undefined && next !== valueRef.current) onChange(next);
      }, 80);
    };
    el.addEventListener("scroll", handle, { passive: true });
    return () => {
      el.removeEventListener("scroll", handle);
      if (timer) clearTimeout(timer);
    };
  }, [items, onChange]);

  // Tap an item → that becomes the wheel's value and the picker closes.
  // No need to scroll first; the picker is unmounting anyway.
  const onItemClick = (idx: number) => {
    onTap(items[idx].value);
  };

  const padHeight = ITEM_HEIGHT;
  const wheelH = VISIBLE_COUNT * ITEM_HEIGHT;

  return (
    <div className="cg-score-pick-col" style={{ width: WHEEL_WIDTH }}>
      <div className="cg-score-pick-label">{label}</div>
      <div
        className="cg-score-pick-wheel"
        style={{ height: wheelH, width: WHEEL_WIDTH }}
      >
        <div
          className="cg-score-pick-indicator"
          style={{ top: padHeight, height: ITEM_HEIGHT }}
        />
        <div
          ref={scrollerRef}
          className="cg-score-pick-scroller"
          style={{ height: wheelH }}
        >
          <div style={{ height: padHeight }} aria-hidden />
          {items.map((it, i) => (
            <div
              key={it.value}
              className={`cg-score-pick-item${
                it.value === value ? " cg-score-pick-item-on" : ""
              }`}
              style={{ height: ITEM_HEIGHT }}
              onClick={() => onItemClick(i)}
            >
              {it.label}
            </div>
          ))}
          <div style={{ height: padHeight }} aria-hidden />
        </div>
      </div>
    </div>
  );
}
